import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'

class ApiService {
  private client: AxiosInstance
  private isRefreshing = false
  private failedQueue: Array<{ resolve: Function; reject: Function }> = []

  constructor() {
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:9100/api',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Interceptor para agregar token de autorización (con refresh proactivo)
    this.client.interceptors.request.use(
      async (config) => {
        const authStore = useAuthStore()
        if (authStore.isInitialized) {
          await authStore.refreshToken()
        }
        if (authStore.accessToken) {
          config.headers.Authorization = `Bearer ${authStore.accessToken}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Interceptor para manejar respuestas y errores
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        const authStore = useAuthStore()
        
        // Evitar bucles infinitos
        if (originalRequest._retry || originalRequest.url?.includes('/auth/')) {
          return Promise.reject(error)
        }
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Si ya se está renovando, agregar a la cola
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject })
            }).then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`
              return this.client(originalRequest)
            }).catch(err => {
              return Promise.reject(err)
            })
          }

          originalRequest._retry = true
          this.isRefreshing = true

          try {
            const success = await authStore.refreshToken()
            if (success && authStore.accessToken) {
              // Procesar cola de peticiones fallidas
              this.processQueue(null, authStore.accessToken)
              
              // Reintentar petición original
              originalRequest.headers.Authorization = `Bearer ${authStore.accessToken}`
              return this.client(originalRequest)
            } else {
              throw new Error('Token refresh failed')
            }
          } catch (refreshError) {
            // Procesar cola con error
            this.processQueue(refreshError, null)
            
            // Cerrar sesión y redirigir
            await authStore.logout()
            if (window.location.pathname !== '/login') {
              window.location.href = '/login'
            }
            return Promise.reject(refreshError)
          } finally {
            this.isRefreshing = false
          }
        }
        
        return Promise.reject(error)
      }
    )
  }

  private processQueue(error: any, token: string | null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error)
      } else {
        resolve(token)
      }
    })
    
    this.failedQueue = []
  }

  // Métodos HTTP básicos
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.get(url, config)
      return response.data
    } catch (error: any) {
      // Manejo específico para errores de rate limiting
      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded, waiting before retry...')
        await this.delay(2000) // Esperar 2 segundos
        throw new Error('Demasiadas peticiones. Por favor, espera un momento.')
      }
      throw error
    }
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.post(url, data, config)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded, waiting before retry...')
        await this.delay(2000)
        throw new Error('Demasiadas peticiones. Por favor, espera un momento.')
      }
      throw error
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.put(url, data, config)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded, waiting before retry...')
        await this.delay(2000)
        throw new Error('Demasiadas peticiones. Por favor, espera un momento.')
      }
      throw error
    }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.patch(url, data, config)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded, waiting before retry...')
        await this.delay(2000)
        throw new Error('Demasiadas peticiones. Por favor, espera un momento.')
      }
      throw error
    }
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    try {
      const response = await this.client.delete(url, config)
      return response.data
    } catch (error: any) {
      if (error.response?.status === 429) {
        console.warn('Rate limit exceeded, waiting before retry...')
        await this.delay(2000)
        throw new Error('Demasiadas peticiones. Por favor, espera un momento.')
      }
      throw error
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // Métodos específicos para la API

  // Autenticación
  async checkAuth() {
    return this.get('/auth/me')
  }

  async logout() {
    try {
      return await this.post('/auth/logout')
    } catch (error) {
      // No fallar si el logout del backend falla
      console.warn('Backend logout failed:', error)
      return null
    }
  }

  // Usuarios
  async getUsers(params?: {
    page?: number
    limit?: number
    role?: string
    search?: string
  }) {
    return this.get('/users', { params })
  }

  async getUserById(id: string) {
    return this.get(`/users/${id}`)
  }

  async createUser(userData: {
    username: string
    email: string
    firstName: string
    lastName: string
    password: string
    role: string
    establecimiento?: string
    dni?: string
    telefono?: string
  }) {
    return this.post('/users', userData)
  }

  async updateUser(id: string, userData: any) {
    return this.put(`/users/${id}`, userData)
  }

  async deleteUser(id: string) {
    return this.delete(`/users/${id}`)
  }

  async changeUserPassword(id: string, password: string, temporary: boolean = false) {
    return this.patch(`/users/${id}/password`, { password, temporary })
  }

  async getUserStats() {
    return this.get('/users/stats/overview')
  }

  // Keycloak
  async getKeycloakUsers(first: number = 0, max: number = 100) {
    return this.get('/keycloak/users', { params: { first, max } })
  }

  async getKeycloakUserById(id: string) {
    return this.get(`/keycloak/users/${id}`)
  }

  async getUserRoles(id: string) {
    return this.get(`/keycloak/users/${id}/roles`)
  }

  async assignUserRole(id: string, roleName: string) {
    return this.post(`/keycloak/users/${id}/roles`, { roleName })
  }

  async removeUserRole(id: string, roleName: string) {
    return this.delete(`/keycloak/users/${id}/roles/${roleName}`)
  }

  async getGroups() {
    return this.get('/keycloak/groups')
  }

  async getUserGroups(id: string) {
    return this.get(`/keycloak/users/${id}/groups`)
  }

  async addUserToGroup(id: string, groupId: string) {
    return this.post(`/keycloak/users/${id}/groups`, { groupId })
  }

  async removeUserFromGroup(id: string, groupId: string) {
    return this.delete(`/keycloak/users/${id}/groups/${groupId}`)
  }

  // Estadísticas
  async getStatistics() {
    return this.get('/statistics/overview')
  }

  async getValidacionStats() {
    return this.get('/statistics/validacion')
  }

  async getDigitacionStats() {
    return this.get('/statistics/digitacion')
  }

  async getTransmisionStats() {
    return this.get('/statistics/transmision')
  }

  async getEstablishmentStats() {
    return this.get('/statistics/users/establishments')
  }

  // CSV
  async exportUsersCSV() {
    const response = await this.client.get('/csv/export', {
      responseType: 'blob'
    })
    return response
  }

  async importUsersFromCSV(formData: FormData) {
    return this.post('/users/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  async downloadCSVTemplate() {
    const response = await this.client.get('/csv/template', {
      responseType: 'blob'
    })
    return response
  }
}

export const apiService = new ApiService()
export default apiService
